import React from "react";
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer";
import { InfiniteLoader } from "react-virtualized/dist/commonjs/InfiniteLoader";
import { List, ListRowProps } from "react-virtualized/dist/commonjs/List";
import { CellMeasurer, CellMeasurerCache } from "react-virtualized/dist/commonjs/CellMeasurer";
import { themeOptions } from "styles/theme";
import { merge, $, keyframes } from "glamor";
import { Loader } from "./loader";
import RaisedButton from "material-ui/RaisedButton";
import ArrowUpward from "material-ui/svg-icons/navigation/arrow-upward";
import classNames from "classnames";
import { formatMessage } from "lib/globalize-util";
import { useFeedPolling, ONE_MINUTE } from "./useFeedPolling";
import { DATATEST } from "constants/integration";
import { Markdown } from "react-showdown";
import Badge from "material-ui/Badge";
import { Link } from "react-router";
import { IExportNotitications, IExportNotificationStatusParams } from "reducers/notifications";
import { IExport } from "containers/folders/types/export";

interface IProps<T> {
    entries: T[];
    loadNextPage: () => void;
    hasNextPage: boolean;
    isNextPageLoading: boolean;
    rowRenderer: (params: ListRowProps & { data: T }) => JSX.Element;
    onLoadPolledEntries?: () => void;
    pollingURL?: string;
    onPollingSuccess?: (response: any) => boolean;
    onPollingFailure?: (error: any) => void;
    pollingInterval?: number;
    pollingRetryCount?: number;
    dimensions?: { height: number; width: number };
    noRowsRenderer?: () => JSX.Element;
    classes?: {
        entry?: string;
        entryContainer?: string;
    };
    exportNotifications?: IExportNotitications;
    onExportWhiteboards?: (exportItem: IExport) => void;
    onUpdateExportNotificationStatus?: (params: IExportNotificationStatusParams) => void;
}

export function ActivityFeed<T = any>({
    entries,
    loadNextPage,
    hasNextPage,
    isNextPageLoading,
    rowRenderer,
    onLoadPolledEntries,
    pollingURL,
    onPollingSuccess,
    onPollingFailure,
    pollingInterval,
    pollingRetryCount,
    dimensions,
    noRowsRenderer,
    classes,
    exportNotifications,
    onExportWhiteboards,
    onUpdateExportNotificationStatus,
}: IProps<T>) {
    const styles = React.useMemo(() => {
        const flashKeyframes = keyframes("flashLatestEntries", {
            from: { backgroundColor: "lightyellow" },
            to: { backgroundColor: "white" },
        });

        return merge(
            {
                height: "100%",
                backgroundColor: "white",
            },
            $(" .entryContainer", {
                padding: "0px 12px",
                wordBreak: "break-all",
            }),
            $(" .entry", {
                borderBottom: `1px ${themeOptions.palette.borderColor} solid`,
                maxWidth: "640px",
                padding: "12px 16px 2px 16px",
                height: "100%",
                display: "flex",
                backgroundColor: `${themeOptions.palette.canvasColor}`,
            }),
            $(" .latestPageButton", {
                position: "absolute",
                width: "100%",
                textAlign: "center",
                top: 5,
                transition: "top .3s ease-in-out",
                display: "none",
            }),
            $(" .showLatestPageButton", {
                display: "block",
            }),
            $(" .flashLatestEntries", {
                animation: `${flashKeyframes} 5s`,
            }),
            $(" .entryNotifications", {
                borderBottom: `1px ${themeOptions.palette.borderColor} solid !important`,
                maxWidth: "640px",
                padding: "12px 16px 2px 16px !important",
                display: "flex !important",
            }),
            $(" .entryNotifications", {
                borderBottom: `1px ${themeOptions.palette.borderColor} solid !important`,
                maxWidth: "640px",
                padding: "12px 16px 2px 16px !important",
                display: "flex !important",
            }),
        );
    }, []);

    const stylesNotitications = React.useMemo(() => {
        return merge(
            {
                display: "flex",
                width: "100%",
            },

            $(" .subtitleContainer", {
                margin: "0 8px 8px 8px",
            }),
            $(" .subtitle", {
                lineHeight: "20px",
                fontSize: "12px",
                color: themeOptions.palette.darkGrey,
            }),
            $(" p", {
                fontSize: "14px",
                margin: 0,
            }),
            $(" strong", {
                fontWeight: "bold",
            }),
            $(" em", {
                fontStyle: "italic",
            }),
            $(" a", {
                color: themeOptions.palette.darkGrey,
            }),
            $(" .leftSide", {
                minWidth: "25px !important",
                padding: "5px 0 0 0 !important",
                float: "left",
            }),
            $(" .leftSide div", {
                padding: "24px 0 12px 12px !important",
            }),
            $(" .linkContainer p", {
                textDecoration: "underline",
                color: "black",
                cursor: "pointer",
            }),
            $(" .unseenEntry", {
                height: "10px",
                width: "10px",
                borderRadius: "50%",
                backgroundColor: themeOptions.palette.accent1Color,
                position: "absolute",
                left: "20px",
            }),
            $(" .rightText", {
                widht: "80%",
                float: "left",
            }),
        );
    }, []);

    const loadingIndexRef = React.useRef<number>();
    const listRef = React.useRef<List>();
    const [loadLatestPageButton, setLoadLatestPageButton] = React.useState<boolean>(false);
    const [flashLatestEntries, setFlashNewEntries] = React.useState<boolean>(false);
    const [scrollIndex, setScrollIndex] = React.useState<number>();

    const cache = React.useMemo(() => {
        return new CellMeasurerCache({
            defaultHeight: window ? window.innerHeight : 40,
            fixedWidth: true,
            minHeight: 40,
        });
    }, [entries?.[0] || "_initial"]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        listRef.current.scrollToPosition(0);
    }, []);

    React.useEffect(() => {
        if (loadingIndexRef.current && entries[loadingIndexRef.current]) {
            cache.clear(loadingIndexRef.current, 0);
            loadingIndexRef.current = undefined;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries]);

    const isRowLoaded = React.useCallback(({ index }) => !!entries?.[index], [entries]);

    const loadMoreRows = React.useCallback(() => {
        if (!isNextPageLoading) {
            loadNextPage();
        }

        return null;
    }, [isNextPageLoading, loadNextPage]);

    const rowCount = React.useMemo(() => {
        return hasNextPage ? entries.length + 1 : entries.length;
    }, [hasNextPage, entries]);

    const handleRenderRow = React.useCallback(
        (params) => {
            const { index, key, style, parent } = params;
            let content;
            if (!isRowLoaded({ index })) {
                content = <Loader />;
                loadingIndexRef.current = index;
            } else {
                content = rowRenderer({ ...params, data: entries[index] });
            }

            return (
                <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
                    <div
                        data-test={DATATEST.ASSORTMENTS.UPDATES_ENTRY}
                        style={style}
                        className={classNames("entryContainer", classes?.entryContainer, { flashLatestEntries })}
                    >
                        <div className={classNames("entry", classes?.entry, { flashLatestEntries })}>{content}</div>
                    </div>
                </CellMeasurer>
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entries, isRowLoaded, flashLatestEntries, rowRenderer],
    );

    const handleLoadLatestEntries = React.useCallback(() => {
        onLoadPolledEntries();
        setLoadLatestPageButton(false);
        setFlashNewEntries(true);
        cache.clearAll();
        setScrollIndex(0);
        listRef.current.scrollToPosition(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onLoadPolledEntries]);

    const handlePollingSuccess = React.useCallback(
        (response) => {
            if (onPollingSuccess) {
                const differenceDetected = onPollingSuccess(response);
                setFlashNewEntries(false);
                setLoadLatestPageButton(differenceDetected);
            }
        },
        [onPollingSuccess],
    );

    const handleScroll = React.useCallback(
        ({ scrollTop }) => {
            if (scrollTop > 0 && flashLatestEntries) {
                setFlashNewEntries(false);
            }
            if (scrollIndex === 0) {
                setScrollIndex(undefined);
            }
        },
        [flashLatestEntries, scrollIndex],
    );

    useFeedPolling({
        url: pollingURL,
        onSuccess: onPollingSuccess && handlePollingSuccess,
        onFailure: onPollingFailure,
        interval: pollingInterval || ONE_MINUTE,
        retryCount: pollingRetryCount || 0,
    });

    const Content: React.FunctionComponent<{ markup: string; createdAt: number; currentTime: number }> = ({
        markup,
        createdAt,
        currentTime,
    }) => {
        return (
            <React.Fragment>
                <Markdown markup={markup} />
                <div className="subtitleContainer">
                    <div className="subtitle" title={new Date(createdAt).toLocaleString()}>
                        {new Date(currentTime).toLocaleString()}
                    </div>
                </div>
            </React.Fragment>
        );
    };

    const handleUpdateExportNotificationStatus = (exportedItem): void => {
        if (!exportedItem.is_read) {
            onUpdateExportNotificationStatus({
                read: 1,
                seen: exportNotifications.exports.filter((item) => !item.is_seen).length,
                export_id: exportedItem.id,
            });
        }
    };

    const handleExportNotificationStatus = (exportedItem): void => {
        if (!exportedItem.is_read) {
            onUpdateExportNotificationStatus({
                read: 1,
                seen: exportNotifications.exports.filter((item) => !item.is_seen).length,
                export_id: exportedItem.id,
            });
            onExportWhiteboards(exportedItem);
        }
    };

    const badgeStyle = React.useMemo(() => {
        const active = {
            backgroundColor: themeOptions.palette.primary1Color,
            height: 10,
            width: 10,
        };
        return {
            active,
            inactive: {
                ...active,
                display: "none",
            },
        };
    }, []);

    return (
        <div {...styles}>
            <div className="exportNotificationsContainer">
                {exportNotifications &&
                    exportNotifications.exports.map((exportObject) => {
                        if (!exportObject.error && !exportObject.files.length) {
                            return (
                                <div
                                    key={exportObject.id}
                                    {...stylesNotitications}
                                    className={classNames("entryNotifications", { unreadEntry: !exportObject.is_read })}
                                >
                                    <div className="leftSide">
                                        <Badge
                                            badgeContent=""
                                            badgeStyle={exportObject.is_seen ? badgeStyle.inactive : badgeStyle.active}
                                        ></Badge>
                                    </div>

                                    <div className="rightText">
                                        <div>
                                            <Content
                                                markup={formatMessage("Your whiteboards bulk export is ready")}
                                                currentTime={exportObject.createdOn}
                                                createdAt={exportObject.createdOn}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        } else if (exportObject.error) {
                            return (
                                <div
                                    key={exportObject.id}
                                    {...stylesNotitications}
                                    className={classNames("entryNotifications", { unreadEntry: !exportObject.is_read })}
                                >
                                    <div className="leftSide">
                                        <Badge
                                            badgeContent=""
                                            badgeStyle={exportObject.is_seen ? badgeStyle.inactive : badgeStyle.active}
                                        ></Badge>
                                    </div>
                                    <div className="rightText">
                                        <p>Your whiteboards bulk export failed.</p>
                                        <Link
                                            to={exportObject.files[0]}
                                            target="_blank"
                                            className="linkContainer"
                                            onClick={handleExportNotificationStatus.bind(this, exportObject)}
                                        >
                                            <Content
                                                markup={formatMessage("Try this export again")}
                                                currentTime={exportObject.createdOn}
                                                createdAt={exportObject.createdOn}
                                            />
                                        </Link>
                                    </div>
                                </div>
                            );
                        } else if (exportObject.files.length) {
                            return (
                                <div
                                    key={exportObject.id}
                                    {...stylesNotitications}
                                    className={classNames("entryNotifications", { unreadEntry: !exportObject.is_read })}
                                >
                                    <div className="leftSide">
                                        <Badge
                                            badgeContent=""
                                            badgeStyle={exportObject.is_seen ? badgeStyle.inactive : badgeStyle.active}
                                        ></Badge>
                                    </div>
                                    <div className="rightText">
                                        <p>Your whiteboards bulk export is ready.</p>
                                        <Link
                                            to={exportObject.files[0]}
                                            target="_blank"
                                            className="linkContainer"
                                            onClick={handleUpdateExportNotificationStatus.bind(this, exportObject)}
                                        >
                                            <Content
                                                markup={formatMessage("Download the XLSX File")}
                                                currentTime={exportObject.createdOn}
                                                createdAt={exportObject.createdOn}
                                            />
                                        </Link>
                                    </div>
                                </div>
                            );
                        }
                    })}
            </div>
            <InfiniteLoader isRowLoaded={isRowLoaded} loadMoreRows={loadMoreRows} rowCount={rowCount}>
                {({ onRowsRendered, registerChild }) => (
                    <AutoSizer>
                        {({ height, width }) => {
                            if (listRef.current) {
                                (listRef.current as any)._registerList = registerChild;
                            }
                            return (
                                <List
                                    ref={listRef}
                                    onRowsRendered={onRowsRendered}
                                    rowRenderer={handleRenderRow}
                                    width={dimensions ? dimensions.width : width}
                                    height={dimensions ? dimensions.height : height}
                                    rowCount={rowCount}
                                    deferredMeasurementCache={cache}
                                    rowHeight={cache.rowHeight}
                                    onScroll={handleScroll}
                                    noRowsRenderer={noRowsRenderer}
                                    scrollToIndex={scrollIndex}
                                />
                            );
                        }}
                    </AutoSizer>
                )}
            </InfiniteLoader>
            <div className={classNames("latestPageButton", { showLatestPageButton: loadLatestPageButton })}>
                <RaisedButton
                    label={formatMessage("See new entries")}
                    icon={<ArrowUpward />}
                    onClick={handleLoadLatestEntries}
                />
            </div>
        </div>
    );
}
